import React, { useState, useEffect } from 'react';
import {
  submitSnapshot, getLatestSnapshot, listSnapshots,
  getMemories, saveMemory,
  getTasks, createTask, updateTaskStatus,
  getDecisions, saveDecision,
  chatBrain,
  getGitHubSummary, getGitHubCommits, getGitHubFile, searchGitHubCode, getGitHubStructure,
  getAzureHealth, getAzureCosts, getAzureDeployments,
  generatePrompt,
  triggerAutoSnapshot, getSnapshotDiff
} from '../services/brainService';

// ─── Design System ───────────────────────────────────────────────────────────
const THEME = {
  primary: '#064e3b',
  gold: '#b48c2c',
  bg: '#f8fafc',
  white: '#ffffff',
};

const PRIORITY_COLORS = { P0: '#dc2626', P1: '#f59e0b', P2: '#3b82f6', P3: '#6b7280' };
const STATUS_COLORS = { open: '#3b82f6', in_progress: '#f59e0b', blocked: '#dc2626', done: '#10b981' };

const MEMORY_TYPES = ['strategic', 'technical', 'operational', 'decision', 'execution'];
const AREAS = ['global', 'frontend', 'backend', 'azure', 'swimvision', 'trackvision', 'billing', 'product'];

// ─── Main Page ───────────────────────────────────────────────────────────────
const BrainPage = () => {
  const [tab, setTab] = useState('snapshot');
  const [loading, setLoading] = useState(false);

  // Snapshot state
  const [snapshotText, setSnapshotText] = useState('');
  const [latestSnapshot, setLatestSnapshot] = useState(null);
  const [snapshotHistory, setSnapshotHistory] = useState([]);

  // Memory state
  const [memories, setMemories] = useState([]);
  const [memoryForm, setMemoryForm] = useState({ memoryType: 'strategic', title: '', content: '' });

  // Tasks state
  const [tasks, setTasks] = useState([]);
  const [taskForm, setTaskForm] = useState({ title: '', area: 'backend', priority: 'P2', description: '' });

  // Decisions state
  const [decisions, setDecisions] = useState([]);
  const [decisionForm, setDecisionForm] = useState({ decision: '', rationale: '', impactArea: 'architecture' });

  // Chat state
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);

  // GitHub state
  const [ghSummary, setGhSummary] = useState(null);
  const [ghSearchQuery, setGhSearchQuery] = useState('');
  const [ghSearchResults, setGhSearchResults] = useState([]);
  const [ghFileContent, setGhFileContent] = useState(null);

  // Azure state
  const [azHealth, setAzHealth] = useState(null);
  const [azCosts, setAzCosts] = useState(null);

  // Prompt Generator state
  const [promptTask, setPromptTask] = useState('');
  const [promptResult, setPromptResult] = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);

  // Auto-Snapshot state
  const [autoSnapshotResult, setAutoSnapshotResult] = useState(null);
  const [snapshotDiff, setSnapshotDiff] = useState(null);

  useEffect(() => { loadTabData(tab); }, [tab]);

  const loadTabData = async (t) => {
    setLoading(true);
    try {
      if (t === 'snapshot') {
        const [latest, history] = await Promise.all([
          getLatestSnapshot('global'),
          listSnapshots(10)
        ]);
        setLatestSnapshot(latest?.data);
        setSnapshotHistory(history?.data || []);
      } else if (t === 'memory') {
        const res = await getMemories(null, 30);
        setMemories(res?.data || []);
      } else if (t === 'tasks') {
        const res = await getTasks({ limit: 50 });
        setTasks(res?.data || []);
      } else if (t === 'decisions') {
        const res = await getDecisions(30);
        setDecisions(res?.data || []);
      } else if (t === 'github') {
        const res = await getGitHubSummary();
        setGhSummary(res?.data || null);
      } else if (t === 'azure') {
        const [health, costs] = await Promise.all([getAzureHealth(), getAzureCosts(30)]);
        setAzHealth(health?.data || null);
        setAzCosts(costs?.data || null);
      } else if (t === 'auto') {
        const diff = await getSnapshotDiff();
        setSnapshotDiff(diff?.data || null);
      }
    } catch (e) { console.error('[Brain] load error:', e); }
    setLoading(false);
  };

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleSubmitSnapshot = async () => {
    if (!snapshotText.trim()) return;
    setLoading(true);
    try {
      const res = await submitSnapshot(snapshotText);
      if (res.success) {
        setSnapshotText('');
        loadTabData('snapshot');
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSaveMemory = async () => {
    if (!memoryForm.title || !memoryForm.content) return;
    setLoading(true);
    try {
      await saveMemory(memoryForm);
      setMemoryForm({ memoryType: 'strategic', title: '', content: '' });
      loadTabData('memory');
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleCreateTask = async () => {
    if (!taskForm.title) return;
    setLoading(true);
    try {
      await createTask(taskForm);
      setTaskForm({ title: '', area: 'backend', priority: 'P2', description: '' });
      loadTabData('tasks');
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleSaveDecision = async () => {
    if (!decisionForm.decision) return;
    setLoading(true);
    try {
      await saveDecision(decisionForm);
      setDecisionForm({ decision: '', rationale: '', impactArea: 'architecture' });
      loadTabData('decisions');
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const handleChat = async () => {
    if (!chatMessage.trim()) return;
    const msg = chatMessage;
    setChatMessage('');
    setChatHistory(prev => [...prev, { role: 'user', text: msg }]);
    try {
      const res = await chatBrain(msg, chatHistory);
      if (res.success) {
        setChatHistory(prev => [...prev, { role: 'assistant', text: res.data.reply }]);
      }
    } catch (e) {
      setChatHistory(prev => [...prev, { role: 'assistant', text: 'Erro ao conectar com Brain.' }]);
    }
  };

  const handleTaskStatus = async (id, newStatus) => {
    await updateTaskStatus(id, newStatus);
    loadTabData('tasks');
  };

  // GitHub handlers
  const handleGhSearch = async () => {
    if (!ghSearchQuery.trim()) return;
    try {
      const res = await searchGitHubCode(ghSearchQuery);
      setGhSearchResults(res?.data || []);
    } catch (e) { console.error(e); }
  };

  const handleGhReadFile = async (repo, path) => {
    try {
      const res = await getGitHubFile(repo, path);
      setGhFileContent(res?.data || null);
    } catch (e) { console.error(e); }
  };

  // Prompt generator handler
  const handleGeneratePrompt = async () => {
    if (!promptTask.trim()) return;
    setPromptLoading(true);
    try {
      const res = await generatePrompt(promptTask);
      setPromptResult(res?.data || null);
    } catch (e) { console.error(e); }
    setPromptLoading(false);
  };

  // Auto-snapshot handler
  const handleAutoSnapshot = async () => {
    setLoading(true);
    try {
      const res = await triggerAutoSnapshot();
      setAutoSnapshotResult(res?.data || null);
      // Refresh diff
      const diff = await getSnapshotDiff();
      setSnapshotDiff(diff?.data || null);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  // ─── Render ──────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'snapshot', label: '📸 Fotografia' },
    { id: 'tasks', label: '✅ Tarefas' },
    { id: 'memory', label: '🧠 Memória' },
    { id: 'decisions', label: '⚖️ Decisões' },
    { id: 'chat', label: '💬 Brain Chat' },
    { id: 'github', label: '🐙 GitHub' },
    { id: 'azure', label: '☁️ Azure' },
    { id: 'prompt', label: '⚡ Prompt Gen' },
    { id: 'auto', label: '🔄 Auto-Snapshot' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: THEME.bg }}>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl flex items-center justify-center text-2xl"
                 style={{ backgroundColor: THEME.primary }}>
              🧠
            </div>
            <div>
              <h1 className="text-2xl font-extrabold" style={{ color: THEME.primary }}>
                VUKZ Brain
              </h1>
              <p className="text-gray-500 text-sm">Project Intelligence Layer — Founder Only</p>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-2 mt-4 overflow-x-auto">
            {tabs.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                className={`px-4 py-2 rounded-lg text-sm font-semibold whitespace-nowrap transition-all ${
                  tab === t.id
                    ? 'text-white'
                    : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
                }`}
                style={tab === t.id ? { backgroundColor: THEME.primary } : {}}>
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        {loading && <div className="text-center py-8 text-gray-400">Carregando...</div>}

        {/* ─── Snapshot Tab ─────────────────────────────────────────── */}
        {tab === 'snapshot' && !loading && (
          <div className="space-y-6">
            {/* Submit new snapshot */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold text-lg mb-3" style={{ color: THEME.primary }}>
                Nova Fotografia do Projeto
              </h2>
              <textarea
                value={snapshotText}
                onChange={(e) => setSnapshotText(e.target.value)}
                placeholder="Cole aqui a fotografia atual do projeto (status, bloqueios, prioridades, riscos)..."
                className="w-full h-40 p-3 border border-gray-200 rounded-lg text-sm resize-y"
              />
              <button onClick={handleSubmitSnapshot} disabled={!snapshotText.trim()}
                className="mt-3 px-6 py-2 rounded-lg text-white font-bold disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}>
                Salvar & Analisar com IA
              </button>
            </div>

            {/* Latest snapshot */}
            {latestSnapshot && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-bold text-lg mb-3" style={{ color: THEME.primary }}>
                  Última Fotografia ({latestSnapshot.area})
                </h2>
                <p className="text-sm text-gray-600 mb-4">{latestSnapshot.summary}</p>

                {/* Status grid */}
                {latestSnapshot.structured_status && Object.keys(latestSnapshot.structured_status).length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
                    {Object.entries(latestSnapshot.structured_status).map(([mod, status]) => (
                      <div key={mod} className="bg-gray-50 rounded-lg p-2 text-center">
                        <div className="text-xs font-semibold text-gray-700">{mod}</div>
                        <div className={`text-xs font-bold ${
                          status === 'operacional' ? 'text-green-600' :
                          status === 'parcial' ? 'text-yellow-600' :
                          status === 'atencao' ? 'text-orange-600' :
                          status === 'quebrado' ? 'text-red-600' : 'text-gray-400'
                        }`}>{status}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Blockers */}
                {latestSnapshot.blockers?.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-sm font-bold text-red-600 mb-1">🚫 Bloqueios</h3>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {latestSnapshot.blockers.map((b, i) => <li key={i}>• {b}</li>)}
                    </ul>
                  </div>
                )}

                {/* Priorities */}
                {latestSnapshot.priorities?.length > 0 && (
                  <div className="mb-3">
                    <h3 className="text-sm font-bold text-blue-600 mb-1">🎯 Prioridades</h3>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {latestSnapshot.priorities.map((p, i) => <li key={i}>{i + 1}. {p}</li>)}
                    </ul>
                  </div>
                )}

                {/* Decisions needed */}
                {latestSnapshot.decisions_needed?.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-amber-600 mb-1">⚖️ Decisões Pendentes</h3>
                    <ul className="text-sm text-gray-700 space-y-1">
                      {latestSnapshot.decisions_needed.map((d, i) => <li key={i}>• {d}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* History */}
            {snapshotHistory.length > 0 && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>Histórico</h2>
                <div className="space-y-2">
                  {snapshotHistory.map(s => (
                    <div key={s.id} className="flex justify-between items-center bg-gray-50 rounded-lg p-3">
                      <div>
                        <span className="text-sm font-semibold">{s.area}</span>
                        <span className="text-xs text-gray-400 ml-2">{s.summary?.substring(0, 80)}...</span>
                      </div>
                      <span className="text-xs text-gray-400">{s.snapshot_date?.substring(0, 10)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── Tasks Tab ────────────────────────────────────────────── */}
        {tab === 'tasks' && !loading && (
          <div className="space-y-6">
            {/* Create task */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>Nova Tarefa</h2>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <input value={taskForm.title} onChange={e => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="Título" className="sm:col-span-2 px-3 py-2 border rounded-lg text-sm" />
                <select value={taskForm.priority} onChange={e => setTaskForm({ ...taskForm, priority: e.target.value })}
                  className="px-3 py-2 border rounded-lg text-sm">
                  {['P0', 'P1', 'P2', 'P3'].map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={taskForm.area} onChange={e => setTaskForm({ ...taskForm, area: e.target.value })}
                  className="px-3 py-2 border rounded-lg text-sm">
                  {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
                </select>
              </div>
              <textarea value={taskForm.description} onChange={e => setTaskForm({ ...taskForm, description: e.target.value })}
                placeholder="Descrição (opcional)" className="w-full mt-2 px-3 py-2 border rounded-lg text-sm h-16 resize-y" />
              <button onClick={handleCreateTask} disabled={!taskForm.title}
                className="mt-2 px-5 py-2 rounded-lg text-white font-bold text-sm disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}>Criar</button>
            </div>

            {/* Task list */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>
                Tarefas ({tasks.length})
              </h2>
              <div className="space-y-2">
                {tasks.map(t => (
                  <div key={t.id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                    <span className="text-xs font-bold px-2 py-0.5 rounded"
                      style={{ backgroundColor: PRIORITY_COLORS[t.priority] + '20', color: PRIORITY_COLORS[t.priority] }}>
                      {t.priority}
                    </span>
                    <div className="flex-1">
                      <div className="text-sm font-semibold">{t.title}</div>
                      <div className="text-xs text-gray-400">{t.area}</div>
                    </div>
                    <select value={t.status} onChange={e => handleTaskStatus(t.id, e.target.value)}
                      className="text-xs px-2 py-1 border rounded"
                      style={{ color: STATUS_COLORS[t.status] }}>
                      {['open', 'in_progress', 'blocked', 'done', 'cancelled'].map(s =>
                        <option key={s} value={s}>{s}</option>
                      )}
                    </select>
                  </div>
                ))}
                {tasks.length === 0 && <p className="text-sm text-gray-400">Nenhuma tarefa.</p>}
              </div>
            </div>
          </div>
        )}

        {/* ─── Memory Tab ───────────────────────────────────────────── */}
        {tab === 'memory' && !loading && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>Nova Memória</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <select value={memoryForm.memoryType} onChange={e => setMemoryForm({ ...memoryForm, memoryType: e.target.value })}
                  className="px-3 py-2 border rounded-lg text-sm">
                  {MEMORY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
                <input value={memoryForm.title} onChange={e => setMemoryForm({ ...memoryForm, title: e.target.value })}
                  placeholder="Título" className="sm:col-span-2 px-3 py-2 border rounded-lg text-sm" />
              </div>
              <textarea value={memoryForm.content} onChange={e => setMemoryForm({ ...memoryForm, content: e.target.value })}
                placeholder="Conteúdo da memória..." className="w-full mt-2 px-3 py-2 border rounded-lg text-sm h-20 resize-y" />
              <button onClick={handleSaveMemory} disabled={!memoryForm.title || !memoryForm.content}
                className="mt-2 px-5 py-2 rounded-lg text-white font-bold text-sm disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}>Salvar</button>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>Memórias Ativas ({memories.length})</h2>
              <div className="space-y-2">
                {memories.map(m => (
                  <div key={m.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="flex justify-between">
                      <span className="text-xs font-bold px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">{m.memory_type}</span>
                      <span className="text-xs text-gray-400">{m.created_at?.substring(0, 10)}</span>
                    </div>
                    <div className="text-sm font-semibold mt-1">{m.title}</div>
                    <div className="text-xs text-gray-500 mt-1 line-clamp-2">{m.content}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── Decisions Tab ────────────────────────────────────────── */}
        {tab === 'decisions' && !loading && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>Registrar Decisão</h2>
              <input value={decisionForm.decision} onChange={e => setDecisionForm({ ...decisionForm, decision: e.target.value })}
                placeholder="Decisão tomada..." className="w-full px-3 py-2 border rounded-lg text-sm mb-2" />
              <textarea value={decisionForm.rationale} onChange={e => setDecisionForm({ ...decisionForm, rationale: e.target.value })}
                placeholder="Racional (por quê?)" className="w-full px-3 py-2 border rounded-lg text-sm h-16 resize-y mb-2" />
              <button onClick={handleSaveDecision} disabled={!decisionForm.decision}
                className="px-5 py-2 rounded-lg text-white font-bold text-sm disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}>Registrar</button>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>Decisões ({decisions.length})</h2>
              <div className="space-y-2">
                {decisions.map(d => (
                  <div key={d.id} className="bg-gray-50 rounded-lg p-3">
                    <div className="text-sm font-semibold">{d.decision}</div>
                    {d.rationale && <div className="text-xs text-gray-500 mt-1">{d.rationale}</div>}
                    <div className="text-xs text-gray-400 mt-1">
                      {d.impact_area} • {d.decided_by} • {d.created_at?.substring(0, 10)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── Chat Tab ─────────────────────────────────────────────── */}
        {tab === 'chat' && (
          <div className="bg-white rounded-xl border border-gray-200 p-6 flex flex-col" style={{ height: '70vh' }}>
            <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>Brain Chat</h2>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto space-y-3 mb-4">
              {chatHistory.length === 0 && (
                <p className="text-sm text-gray-400 text-center mt-10">
                  Pergunte ao Brain sobre o estado do projeto, prioridades, bloqueios ou peça prompts.
                </p>
              )}
              {chatHistory.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-4 py-2 rounded-xl text-sm ${
                    msg.role === 'user'
                      ? 'bg-emerald-900 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    <pre className="whitespace-pre-wrap font-sans">{msg.text}</pre>
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="flex gap-2">
              <input value={chatMessage} onChange={e => setChatMessage(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleChat()}
                placeholder="Qual é o estado atual? O que devo priorizar?"
                className="flex-1 px-4 py-2 border rounded-lg text-sm" />
              <button onClick={handleChat} disabled={!chatMessage.trim()}
                className="px-5 py-2 rounded-lg text-white font-bold disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}>
                Enviar
              </button>
            </div>
          </div>
        )}

        {/* ─── GitHub Tab ───────────────────────────────────────────── */}
        {tab === 'github' && !loading && (
          <div className="space-y-6">
            {/* Search */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>🔍 Buscar no Código</h2>
              <div className="flex gap-2">
                <input value={ghSearchQuery} onChange={e => setGhSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleGhSearch()}
                  placeholder="Buscar função, variável, componente..."
                  className="flex-1 px-4 py-2 border rounded-lg text-sm" />
                <button onClick={handleGhSearch} className="px-5 py-2 rounded-lg text-white font-bold"
                  style={{ backgroundColor: THEME.primary }}>Buscar</button>
              </div>
              {ghSearchResults.length > 0 && (
                <div className="mt-3 space-y-2">
                  {ghSearchResults.map((r, i) => (
                    <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleGhReadFile(r.repo, r.path)}>
                      <span className="text-xs font-mono bg-gray-200 px-2 py-0.5 rounded">{r.repo}</span>
                      <span className="text-sm text-gray-700">{r.path}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* File viewer */}
            {ghFileContent && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-bold text-sm" style={{ color: THEME.primary }}>{ghFileContent.path}</h3>
                  <button onClick={() => setGhFileContent(null)} className="text-xs text-gray-400 hover:text-gray-600">✕ Fechar</button>
                </div>
                <pre className="text-xs bg-gray-900 text-green-300 p-4 rounded-lg overflow-auto max-h-96 font-mono">
                  {ghFileContent.content}
                </pre>
              </div>
            )}

            {/* Summary */}
            {ghSummary && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {Object.entries(ghSummary).map(([repo, info]) => (
                  <div key={repo} className="bg-white rounded-xl border border-gray-200 p-6">
                    <h3 className="font-bold mb-3" style={{ color: THEME.primary }}>{repo}</h3>
                    {info.error ? (
                      <p className="text-sm text-red-500">{info.error}</p>
                    ) : (
                      <>
                        <div className="flex gap-4 mb-3">
                          <div className="bg-gray-50 rounded-lg p-2 text-center flex-1">
                            <div className="text-xl font-bold" style={{ color: THEME.primary }}>{info.commit_count_7d}</div>
                            <div className="text-xs text-gray-500">Commits (7d)</div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-2 text-center flex-1">
                            <div className="text-xl font-bold" style={{ color: THEME.gold }}>{info.open_prs?.length || 0}</div>
                            <div className="text-xs text-gray-500">PRs Abertas</div>
                          </div>
                        </div>
                        <div className="space-y-1">
                          {info.recent_commits?.slice(0, 5).map((c, i) => (
                            <div key={i} className="text-xs text-gray-600 flex gap-2">
                              <span className="font-mono text-gray-400">{c.sha}</span>
                              <span className="truncate">{c.message}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── Azure Tab ────────────────────────────────────────────── */}
        {tab === 'azure' && !loading && (
          <div className="space-y-6">
            {/* Health */}
            {azHealth && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>☁️ Infraestrutura Azure</h2>
                {azHealth.error ? (
                  <p className="text-sm text-red-500">{azHealth.error}</p>
                ) : (
                  <>
                    <div className="flex gap-4 mb-4">
                      <div className="bg-green-50 rounded-lg p-3 text-center flex-1">
                        <div className="text-2xl font-bold text-green-700">{azHealth.running}</div>
                        <div className="text-xs text-green-600">Running</div>
                      </div>
                      <div className="bg-red-50 rounded-lg p-3 text-center flex-1">
                        <div className="text-2xl font-bold text-red-700">{azHealth.stopped}</div>
                        <div className="text-xs text-red-600">Stopped</div>
                      </div>
                      <div className="bg-gray-50 rounded-lg p-3 text-center flex-1">
                        <div className="text-2xl font-bold text-gray-700">{azHealth.total_apps}</div>
                        <div className="text-xs text-gray-500">Total</div>
                      </div>
                    </div>
                    <div className="space-y-2">
                      {azHealth.apps?.map((app, i) => (
                        <div key={i} className="flex items-center gap-3 bg-gray-50 rounded-lg p-2">
                          <div className={`w-2 h-2 rounded-full ${app.state === 'Running' ? 'bg-green-500' : 'bg-red-500'}`} />
                          <span className="text-sm font-semibold flex-1">{app.name}</span>
                          <span className="text-xs text-gray-400">{app.kind}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Costs */}
            {azCosts && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>💰 Custos ({azCosts.period_days}d)</h2>
                {azCosts.error ? (
                  <p className="text-sm text-red-500">{azCosts.error}</p>
                ) : (
                  <>
                    <div className="bg-amber-50 rounded-lg p-4 mb-4 text-center">
                      <div className="text-3xl font-bold" style={{ color: THEME.gold }}>${azCosts.total_usd}</div>
                      <div className="text-xs text-gray-500">Total USD ({azCosts.period_days} dias)</div>
                    </div>
                    <div className="space-y-2">
                      {azCosts.by_service?.map((s, i) => (
                        <div key={i} className="flex justify-between items-center bg-gray-50 rounded-lg p-2">
                          <span className="text-sm">{s.service}</span>
                          <span className="text-sm font-bold">${s.cost_usd}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {!azHealth && !azCosts && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 text-center">
                <p className="text-gray-400">Conecte as credenciais Azure para visualizar infraestrutura.</p>
                <p className="text-xs text-gray-300 mt-2">Variáveis: AZURE_SUBSCRIPTION_ID, AZURE_TENANT_ID, AZURE_MONITOR_CLIENT_ID, AZURE_MONITOR_CLIENT_SECRET</p>
              </div>
            )}
          </div>
        )}

        {/* ─── Prompt Generator Tab ─────────────────────────────────── */}
        {tab === 'prompt' && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>⚡ Gerador de Prompts Cirúrgicos</h2>
              <p className="text-sm text-gray-500 mb-4">
                Descreva a tarefa e o Brain gerará um prompt pronto para colar no Cursor/Copilot/Opus.
              </p>
              <textarea
                value={promptTask}
                onChange={(e) => setPromptTask(e.target.value)}
                placeholder="Ex: Corrigir o overlay do SwimVision que mostra vídeo preto quando não há detecção de landmarks..."
                className="w-full h-32 p-3 border border-gray-200 rounded-lg text-sm resize-y"
              />
              <button onClick={handleGeneratePrompt} disabled={!promptTask.trim() || promptLoading}
                className="mt-3 px-6 py-2 rounded-lg text-white font-bold disabled:opacity-50"
                style={{ backgroundColor: THEME.primary }}>
                {promptLoading ? 'Gerando...' : 'Gerar Prompt'}
              </button>
            </div>

            {promptResult && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-bold" style={{ color: THEME.primary }}>Prompt Gerado</h3>
                  <button onClick={() => navigator.clipboard.writeText(promptResult.prompt)}
                    className="text-xs px-3 py-1 bg-gray-100 rounded-lg hover:bg-gray-200 font-semibold">
                    📋 Copiar
                  </button>
                </div>
                <pre className="text-sm bg-gray-900 text-green-300 p-4 rounded-lg overflow-auto max-h-96 whitespace-pre-wrap font-mono">
                  {promptResult.prompt}
                </pre>
                <div className="mt-2 text-xs text-gray-400">
                  Gerado em {promptResult.generated_at?.substring(0, 19)} • Contexto: {promptResult.context_used?.substring(0, 80)}...
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── Auto-Snapshot Tab ────────────────────────────────────── */}
        {tab === 'auto' && !loading && (
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="font-bold mb-3" style={{ color: THEME.primary }}>🔄 Auto-Snapshot</h2>
              <p className="text-sm text-gray-500 mb-4">
                Gera automaticamente uma fotografia lendo DB, GitHub e Azure. Compara com a anterior.
              </p>
              <button onClick={handleAutoSnapshot}
                className="px-6 py-2 rounded-lg text-white font-bold"
                style={{ backgroundColor: THEME.primary }}>
                Gerar Snapshot Agora
              </button>
            </div>

            {/* Diff */}
            {snapshotDiff && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold mb-3" style={{ color: THEME.primary }}>📊 Comparação com Anterior</h3>
                <p className="text-sm text-gray-600 mb-3">{snapshotDiff.summary}</p>
                {snapshotDiff.changes?.length > 0 ? (
                  <div className="space-y-2">
                    {snapshotDiff.changes.map((c, i) => (
                      <div key={i} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                        <span className="text-amber-500">→</span>
                        <span className="text-sm">{c}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Nenhuma mudança detectada.</p>
                )}
                {snapshotDiff.days_between && (
                  <p className="text-xs text-gray-400 mt-2">Intervalo: {snapshotDiff.days_between} dias entre snapshots</p>
                )}
              </div>
            )}

            {/* Last auto result */}
            {autoSnapshotResult && (
              <div className="bg-white rounded-xl border border-gray-200 p-6">
                <h3 className="font-bold mb-3 text-green-700">✅ Snapshot Gerado</h3>
                <div className="text-sm text-gray-600">
                  <p>ID: {autoSnapshotResult.snapshot?.snapshot_id}</p>
                  <p>Tarefas criadas: {autoSnapshotResult.snapshot?.tasks_created || 0}</p>
                  <p>Cache: {autoSnapshotResult.snapshot?.was_cached ? 'Sim (duplicado)' : 'Não (novo)'}</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BrainPage;
